import { useState } from 'react';
import type { MfaMethod } from '@shared/services';

export type MfaVerifyStep = 'select' | 'verify' | 'expired';

/**
 * The inline MFA-verification state machine, used on `/login` and `/switch`. Steps live entirely in
 * memory — the URL never changes across method selection and verification — so `mfaPending` stays out
 * of history and the ceremony renders in place inside the originating flow.
 *
 * When the challenge advertises a single method there is nothing to choose, so the machine starts
 * directly on that method's `verify` step and `canReturnToPicker` is false (there is no picker to go
 * back to). With two or more methods it starts on `select`.
 *
 * Unlike the old `/mfa` ceremony this hook does NOT adopt the session: a verified factor yields an
 * `ILoginSuccessView` that the container adopts (a login is a guest→authorized transition, a switch is
 * authorized→authorized), so adoption and the post-auth redirect are the caller's responsibility.
 */
export const useMfaVerify = (mfaMethods: MfaMethod[]) => {
  const soleMethod = mfaMethods.length === 1 ? mfaMethods[0] : null;

  const [step, setStep] = useState<MfaVerifyStep>(soleMethod ? 'verify' : 'select');
  const [method, setMethod] = useState<MfaMethod | null>(soleMethod);

  const canReturnToPicker = mfaMethods.length > 1;

  const selectMethod = (chosen: MfaMethod) => {
    setMethod(chosen);
    setStep('verify');
  };

  const backToPicker = () => {
    setMethod(null);
    setStep('select');
  };

  const expire = () => setStep('expired');

  return {
    step,
    method,
    canReturnToPicker,
    selectMethod,
    backToPicker,
    expire,
  };
};
